import React from "react";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import { makeStyles } from "@material-ui/core/styles";

// Editor dos parâmetros de entrada de uma Ferramenta. Cada parâmetro é um CARD
// (uma borda só) no padrão do painel: label acima, valores técnicos em mono, e a
// referência {{...}} vira um chip copiável discreto. A IA preenche os parâmetros
// ao chamar a ferramenta; ficam no fluxo como {{steps.trigger.input.<nome>}}.
const TYPES = [
  { value: "string", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "boolean", label: "Sim/Não" },
];

const CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23929AA4' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")";

const useStyles = makeStyles(theme => {
  const a = theme.app;
  const input = {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${a.border}`,
    borderRadius: 8,
    fontSize: 13.5,
    padding: "9px 11px",
    background: "#fff",
    font: "inherit",
    color: a.ink,
    "&:focus": { outline: "none", borderColor: a.dark, boxShadow: "0 0 0 3px rgba(44,53,61,.08)" },
    "&::placeholder": { color: a.textTertiary },
  };
  return {
    empty: { fontSize: 12.5, color: a.textTertiary, padding: "2px 0 12px" },
    card: { border: `1px solid ${a.border}`, borderRadius: 12, padding: 12, marginBottom: 10, background: "#fff" },
    row: { display: "flex", gap: 8, alignItems: "flex-end" },
    nameCol: { flex: 1, minWidth: 0 },
    typeCol: { width: 118, flexShrink: 0 },
    label: { display: "block", fontSize: 12, fontWeight: 600, color: a.textSecondary, marginBottom: 5 },
    input,
    mono: { fontFamily: a.mono },
    select: {
      ...input,
      appearance: "none", MozAppearance: "none", WebkitAppearance: "none",
      paddingRight: 28,
      backgroundImage: CHEVRON,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 9px center",
      cursor: "pointer",
    },
    del: {
      width: 34, height: 34, flexShrink: 0, borderRadius: 8, color: a.textTertiary,
      border: `1px solid ${a.border}`,
      "&:hover": { color: a.danger, borderColor: a.danger, background: "#fff" },
    },
    fieldGap: { marginTop: 10 },
    fieldHint: { fontSize: 11.5, color: a.textTertiary, marginTop: 5 },
    cardFoot: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 12 },
    // toggle "Obrigatório"
    toggleRow: { display: "flex", alignItems: "center", gap: 8 },
    toggleLabel: { fontSize: 12.5, color: a.textSecondary, fontWeight: 500 },
    sw: { position: "relative", width: 34, height: 20, borderRadius: 999, border: "none", padding: 0, cursor: "pointer", background: "#CBD1D8", transition: "background .15s", flexShrink: 0 },
    swOn: { background: a.dark },
    knob: { position: "absolute", top: 2, left: 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.25)", transition: "transform .15s" },
    knobOn: { transform: "translateX(14px)" },
    addBtn: {
      width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      border: `1px dashed ${a.borderInput}`, borderRadius: 10, background: "#fff",
      color: a.textSecondary, fontWeight: 600, fontSize: 13, padding: "10px", cursor: "pointer",
      "&:hover": { background: a.hover, borderColor: a.textTertiary },
      "& svg": { fontSize: 18 },
    },
  };
});

// nome de variável seguro (sem espaços/acentos) pra usar em {{steps.trigger.input.x}}
const slug = s =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+/, "");

const ToolInputEditor = ({ value, onChange, readOnly }) => {
  const classes = useStyles();
  const params = Array.isArray(value) ? value : [];

  const commit = next => onChange(next);
  const patch = (i, key, v) => commit(params.map((p, idx) => (idx === i ? { ...p, [key]: v } : p)));
  const add = () => commit([...params, { name: "", type: "string", description: "", required: true }]);
  const remove = i => commit(params.filter((_, idx) => idx !== i));

  return (
    <div>
      {params.length === 0 && (
        <Typography className={classes.empty}>Nenhum parâmetro ainda — a ferramenta é chamada sem argumentos.</Typography>
      )}

      {params.map((p, i) => {
        return (
          <div key={i} className={classes.card}>
            <div className={classes.row}>
              <div className={classes.nameCol}>
                <label className={classes.label}>Nome</label>
                <input
                  className={clsxInput(classes)}
                  placeholder="cidade"
                  value={p.name || ""}
                  onChange={e => patch(i, "name", slug(e.target.value))}
                  disabled={readOnly}
                />
              </div>
              <div className={classes.typeCol}>
                <label className={classes.label}>Tipo</label>
                <select
                  className={classes.select}
                  value={p.type || "string"}
                  onChange={e => patch(i, "type", e.target.value)}
                  disabled={readOnly}
                >
                  {TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              {!readOnly && (
                <IconButton className={classes.del} size="small" onClick={() => remove(i)} title="Excluir parâmetro">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>

            <div className={classes.fieldGap}>
              <label className={classes.label}>Descrição</label>
              <input
                className={classes.input}
                placeholder="Cidade para consultar a previsão do tempo"
                value={p.description || ""}
                onChange={e => patch(i, "description", e.target.value)}
                disabled={readOnly}
              />
              <div className={classes.fieldHint}>A IA lê isto para saber o que preencher.</div>
            </div>

            <div className={classes.cardFoot}>
              <div className={classes.toggleRow}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={p.required !== false}
                  className={`${classes.sw} ${p.required !== false ? classes.swOn : ""}`}
                  onClick={() => !readOnly && patch(i, "required", p.required === false)}
                  disabled={readOnly}
                >
                  <span className={`${classes.knob} ${p.required !== false ? classes.knobOn : ""}`} />
                </button>
                <span className={classes.toggleLabel}>Obrigatório</span>
              </div>
            </div>
          </div>
        );
      })}

      {!readOnly && (
        <button type="button" className={classes.addBtn} onClick={add}>
          <AddIcon /> Adicionar parâmetro
        </button>
      )}
    </div>
  );
};

// o input de "Nome" usa fonte mono (valor técnico)
const clsxInput = classes => `${classes.input} ${classes.mono}`;

export default ToolInputEditor;
